import { z } from "zod";

export const formSchema = z
  .object({
    ruleName: z.string().min(1, { message: "Rule name is required" }),
    invoiceValueFrom: z
      .string()
      .min(1, { message: "Invoice value from is required" })
      .refine(
        (val) => {
          const num = Number(val);
          return !isNaN(num) && num > 0;
        },
        {
          message: "Invoice value from must be a valid positive number",
        }
      )
      .refine(
        (val) => {
          const num = Number(val);
          return Number.isInteger(num);
        },
        {
          message: "Decimal values are not allowed",
        }
      ),
    invoiceValueTo: z
      .string()
      .min(1, { message: "Invoice value to is required" })
      .refine(
        (val) => {
          const num = Number(val);
          return !isNaN(num) && num > 0;
        },
        {
          message: "Invoice value to must be a valid positive number",
        }
      )
      .refine(
        (val) => {
          const num = Number(val);
          return Number.isInteger(num);
        },
        {
          message: "Decimal values are not allowed",
        }
      ),
    couponType: z.enum(["flat", "percentage"], {
      required_error: "Please select a coupon type",
    }),
    couponValue: z
      .string()
      .min(1, { message: "Coupon value is required" })
      .refine(
        (val) => {
          const num = Number(val);
          return !isNaN(num) && num >= 0;
        },
        {
          message: "Coupon value cannot be negative",
        }
      ),
    issuanceStartDate: z.date({
      required_error: "Start date is required",
    }),
    issuanceEndDate: z.date({
      required_error: "End date is required",
    }),
    expiryDate: z.date({
      required_error: "Expiry date is required",
    }),
    usageLimit: z.enum(["single", "multiple"], {
      required_error: "Please select a usage limit",
    }),
    redeemAmountFrom: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true;
          const num = Number(val);
          return !isNaN(num) && num > 0;
        },
        {
          message: "Redeem amount from must be a valid positive number",
        }
      )
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true;
          const num = Number(val);
          return Number.isInteger(num);
        },
        {
          message: "Decimal values are not allowed",
        }
      ),
    redeemAmountTo: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true;
          const num = Number(val);
          return !isNaN(num) && num > 0;
        },
        {
          message: "Redeem amount to must be a valid positive number",
        }
      )
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true;
          const num = Number(val);
          return Number.isInteger(num);
        },
        {
          message: "Decimal values are not allowed",
        }
      ),
  })
  .refine(
    (data) => {
      if (data.invoiceValueFrom && data.invoiceValueTo) {
        const from = Number(data.invoiceValueFrom);
        const to = Number(data.invoiceValueTo);
        if (isNaN(from) || isNaN(to)) return true;
        return to > from;
      }
      return true;
    },
    {
      message: "Invoice To Amount must be greater than Invoice From Amount",
      path: ["invoiceValueTo"],
    }
  )
  .refine(
    (data) => {
      if (
        data.redeemAmountFrom &&
        data.redeemAmountFrom.trim() !== "" &&
        data.redeemAmountTo &&
        data.redeemAmountTo.trim() !== ""
      ) {
        const from = Number(data.redeemAmountFrom);
        const to = Number(data.redeemAmountTo);
        return to > from;
      }
      return true;
    },
    {
      message: "Redeem To Amount must be greater than Redeem From Amount",
      path: ["redeemAmountTo"],
    }
  )
  .refine(
    (data) => {
      if (data.issuanceStartDate && data.issuanceEndDate) {
        return data.issuanceStartDate <= data.issuanceEndDate;
      }
      return true;
    },
    {
      message: "Start Date cannot be greater than End Date",
      path: ["issuanceStartDate"],
    }
  )
  .refine(
    (data) => {
      if (data.expiryDate && data.issuanceStartDate) {
        return data.expiryDate >= data.issuanceStartDate;
      }
      return true;
    },
    {
      message: "Expiry Date cannot be earlier than Start Date",
      path: ["expiryDate"],
    }
  )
  .refine(
    (data) => {
      if (data.expiryDate && data.issuanceEndDate) {
        return data.expiryDate >= data.issuanceEndDate;
      }
      return true;
    },
    {
      message: "Expiry Date cannot be earlier than End Date",
      path: ["expiryDate"],
    }
  )
  .refine(
    (data) => {
      if (data.couponType === "percentage" && data.couponValue) {
        const value = Number(data.couponValue);
        if (isNaN(value)) return true;
        return value <= 100;
      }
      return true;
    },
    {
      message: "Percentage coupon value cannot exceed 100",
      path: ["couponValue"],
    }
  );

export type CouponFormValues = z.infer<typeof formSchema>;

export const transactionFormSchema = z.object({
  invoiceNo: z.string().min(1, { message: "Invoice number is required" }),
  invoiceAmount: z.string().min(1, { message: "Invoice amount is required" }),
  mobileNo: z
    .string()
    .min(1, { message: "Mobile number is required" })
    .regex(/^\d+$/, { message: "Mobile number must contain only numbers" })
    .min(7, { message: "Please enter a valid Mobile Number." })
    .max(15, { message: "Mobile number must not exceed 15 digits" })
    .refine(
      (val) => {
        const num = Number(val);
        return !isNaN(num) && num >= 0;
      },
      {
        message: "Mobile number cannot be negative",
      }
    ),
  isCouponRedeem: z.boolean().default(false),
  couponCode: z.string().optional(),
  otp: z.string().optional(),
});

export type TransactionFormValues = z.infer<typeof transactionFormSchema>;

